import { Icon } from '@iconify/react';
import { styled } from '@linaria/react';
import { Tooltip, UnstyledButton } from '@mantine/core';
import { icons } from '../../../assets/icons/icons';
import { newCOLORS } from '../../../styles/colors';
import { H3 } from '../../../ui-library/typography/typography';
import type { ChatHistory } from '../flora.type';

interface FloraHeaderProps {
  isPageView?: boolean;
  selectedChatHistory?: ChatHistory;
  isActiveQuestion: boolean;
  isHistoryOpen: boolean;
  /** True while the open chat has no messages yet; dims the new-chat button. */
  isChatEmpty?: boolean;
  onClose?: () => void;
  onNewChat: () => void;
  onOpenHistory: () => void;
  onCloseHistory: () => void;
  onOpenInFullPage?: () => void;
}

/**
 * Header bar for the Flora aside panel.
 *
 * In the right dock, the title is a button — Flora icon, the open chat's name
 * (or "Flora" when no chat is open yet) and a chevron — that toggles the chat-history dropdown,
 * followed by new-chat, open-in-full-page and close controls. In page view
 * (the page-viewer dock) it renders the chat description as a heading with a
 * new-chat control only.
 *
 * @param {boolean} isPageView - Renders the page-viewer header (chat description heading) instead of the dock header
 * @param {ChatHistory} selectedChatHistory - Active chat whose description titles the header
 * @param {boolean} isActiveQuestion - Disables controls while Flora is answering
 * @param {boolean} isHistoryOpen - Whether the history dropdown is open; toggles the title button handler
 * @param {boolean} [isChatEmpty] - Dims the new-chat button when the chat is already empty
 * @param {() => void} onClose - Closes the aside panel; omit to hide the close button
 * @param {() => void} onNewChat - Starts a new chat
 * @param {() => void} onOpenHistory - Opens the chat history dropdown
 * @param {() => void} onCloseHistory - Closes the chat history dropdown
 * @param {() => void} [onOpenInFullPage] - Navigates to the full-page Flora view; omit to hide the button (e.g. the page-viewer dock)
 * @returns {JSX.Element} The header bar
 */
export const FloraAsideHeader = ({
  isPageView = false,
  selectedChatHistory,
  isActiveQuestion,
  isHistoryOpen,
  isChatEmpty = false,
  onClose,
  onNewChat,
  onOpenHistory,
  onCloseHistory,
  onOpenInFullPage,
}: FloraHeaderProps) => {
  if (!isPageView) {
    const title = selectedChatHistory?.description || 'Flora';
    return (
      <DockHeader>
        <Tooltip label={isActiveQuestion ? 'Please wait for response...' : 'Chat history'} zIndex={600}>
          <TitleButton
            onClick={isHistoryOpen ? onCloseHistory : onOpenHistory}
            disabled={isActiveQuestion}
            aria-label={`Chat history: ${title}`}
            aria-haspopup="dialog"
            aria-expanded={isHistoryOpen}
            data-open={isHistoryOpen}
            data-bf="flora-aside-title-button"
          >
            <img src={icons.iconFlora} alt="" aria-hidden="true" data-bf="flora-aside-icon" />
            <TitleText data-bf="flora-aside-title">{title}</TitleText>
            <Chevron data-open={isHistoryOpen} aria-hidden="true">
              <Icon icon="material-symbols:expand-more" width={18} height={18} />
            </Chevron>
          </TitleButton>
        </Tooltip>
        <DockControls>
          <Tooltip label={isActiveQuestion ? 'Please wait for response...' : 'New chat'} zIndex={600}>
            <DockControlButton
              onClick={onNewChat}
              disabled={isActiveQuestion}
              data-muted={isChatEmpty}
              aria-label="New chat"
            >
              <Icon icon="material-symbols:edit-square-outline" width={20} height={20} />
            </DockControlButton>
          </Tooltip>
          {onOpenInFullPage && (
            <Tooltip label="Open in full page" zIndex={600}>
              <DockControlButton onClick={onOpenInFullPage} disabled={isActiveQuestion} aria-label="Open in full page">
                <Icon icon="material-symbols:open-in-full" width={20} height={20} />
              </DockControlButton>
            </Tooltip>
          )}
          {onClose && (
            <Tooltip label="Close Flora" zIndex={600}>
              <DockControlButton onClick={onClose} aria-label="Close Flora">
                <Icon icon="material-symbols:close" width={20} height={20} />
              </DockControlButton>
            </Tooltip>
          )}
        </DockControls>
      </DockHeader>
    );
  }

  return (
    <Header isPageView={isPageView}>
      <TitleSection>
        <H3 style={{ fontWeight: 1000, fontSize: 20, color: newCOLORS.black }}>
          {selectedChatHistory?.description || 'New Chat'}
        </H3>
      </TitleSection>
      <ControlsSection>
        <Tooltip label={isActiveQuestion ? 'Please wait for response...' : 'New Chat'} zIndex={600}>
          <ControlButton onClick={onNewChat} disabled={isActiveQuestion} aria-label="New chat">
            <Icon icon="material-symbols:add-circle-outline" />
          </ControlButton>
        </Tooltip>
        {onOpenInFullPage && (
          <Tooltip label="Open in Full Page" zIndex={600}>
            <ControlButton onClick={onOpenInFullPage} disabled={isActiveQuestion} aria-label="Open in full page">
              <Icon icon="material-symbols:open-in-full" />
            </ControlButton>
          </Tooltip>
        )}
        {onClose && (
          <Tooltip label="Close Flora" zIndex={600}>
            <ControlButton onClick={onClose} aria-label="Close Flora">
              <Icon icon="material-symbols:close" />
            </ControlButton>
          </Tooltip>
        )}
      </ControlsSection>
    </Header>
  );
};

const Header = styled.div<{ isPageView?: boolean }>`
  display: flex;
  justify-content: space-between;
  flex-direction: row;
  padding: ${(props) => (props.isPageView ? '0 0 12px 0' : '12px 16px')};
  border-top: 0px;
  border-bottom: 1px solid #e9ecef;
  background: white;
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;

  h3 {
    margin: 0;
    font-size: 14px;
    color: #666;
    font-weight: normal;
  }

  img {
    width: 20px;
    height: 20px;
  }
`;

const ControlsSection = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 4px;
`;

const ControlButton = styled(UnstyledButton)`
  width: 28px;
  height: 28px;
  font-size: 23px;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    background: #f5f5f5;
  }

  &:disabled {
    color: ${newCOLORS.gray};
    cursor: not-allowed;
  }
`;

const DockHeader = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px 10px 10px;
  border-bottom: 1px solid var(--sky-light);
  background: var(--base-white);
`;

const TitleButton = styled(UnstyledButton)`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  padding: 4px 8px 4px 6px;
  border-radius: 8px;
  background: transparent;
  transition: background 0.12s;

  img {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }

  &[data-open='true'],
  &:hover:not(:disabled) {
    background: var(--sky-lighter);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const TitleText = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: var(--ink-darkest);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Chevron = styled.span`
  display: flex;
  flex-shrink: 0;
  color: var(--ink-light);
  transition: transform 0.15s ease;

  &[data-open='true'] {
    transform: rotate(180deg);
  }
`;

const DockControls = styled.div`
  display: flex;
  gap: 2px;
  flex-shrink: 0;
`;

const DockControlButton = styled(UnstyledButton)`
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  color: var(--ink-light);
  transition: background 0.12s, color 0.12s;

  /* Dimmed but still legible (WCAG non-text contrast) — the chat is empty
     yet the button stays usable. */
  &[data-muted='true'] {
    color: var(--ink-lighter);
  }

  &:hover:not(:disabled) {
    background: var(--flora-hover-medium);
    color: var(--ink-dark);
  }

  &:disabled {
    color: var(--sky-base);
    cursor: not-allowed;
  }
`;
